import { useInView } from 'framer-motion';
import { type LottieRefCurrentProps } from 'lottie-react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';

import useLottieData from './hooks/useLottieData';

const Lottie = lazy(() => import('lottie-react'));

export interface HeartRateAppUIProps {
  readonly activeIndex: number;
  readonly previousIndex: number;
}

export const getCurrentSegment = ({
  activeIndex,
  previousIndex,
}: {
  activeIndex: number;
  previousIndex: number;
}): [number, number] => {
  const KEYFRAMES: number[] = [66, 93, 109, 129, 220];
  // Handle case where active and previous indices are both 0 when starting from beginning (causes entire animation to play)
  if (activeIndex === 0 && previousIndex === 0) {
    return [0, KEYFRAMES[0]!];
  } else {
    // Prevent it from starting at 0 when refreshing while scrolled down the page
    const skippedSectionsOnLoad = activeIndex - previousIndex > 1;
    const startKeyframe = skippedSectionsOnLoad
      ? KEYFRAMES[activeIndex - 1]!
      : KEYFRAMES[previousIndex]!;
    const endKeyframe = KEYFRAMES[activeIndex]!;
    return [startKeyframe, endKeyframe];
  }
};

const HeartRateAppUI = ({
  activeIndex,
  previousIndex,
}: HeartRateAppUIProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(wrapperRef);
  const { animationData } = useLottieData({
    path: 'timeline/heart-rate',
  });
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [shouldRestartAnimation, setShouldRestartAnimation] =
    useState<boolean>(false);

  const handleDOMLoaded = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    if (isLoaded) {
      if (isInView) {
        const segment = shouldRestartAnimation
          ? { activeIndex: 0, previousIndex: 0 }
          : { activeIndex, previousIndex };
        const currentSegment = getCurrentSegment(segment);
        lottieRef.current?.playSegments(currentSegment, true);
        if (shouldRestartAnimation && activeIndex !== 0) {
          setShouldRestartAnimation(false);
        }
      } else {
        if (!shouldRestartAnimation && activeIndex === 0) {
          setShouldRestartAnimation(true);
        }
      }
    }
  }, [activeIndex, isInView, isLoaded, previousIndex, shouldRestartAnimation]);

  return (
    <div
      className="min-h-px rounded-md bg-[rgba(144,144,144,0.3)] backdrop-blur-md"
      ref={wrapperRef}
    >
      {animationData && (
        <Suspense fallback={<div data-cy="heart_rate_app_loading" />}>
          <Lottie
            animationData={animationData}
            autoplay={false}
            data-cy="heart_rate_app_ui"
            loop={false}
            lottieRef={lottieRef}
            onDOMLoaded={handleDOMLoaded}
          />
        </Suspense>
      )}
    </div>
  );
};

export default HeartRateAppUI;
